// The ONLY channel between this renderer and the host app.
//
// Freeze-first contract: docs/PARTNER_BRIDGE_CONTRACT.md. Nothing else crosses
// — no shared code, no shared state library, no imports in either direction.
// One JSON object per postMessage, over the `NounsHuntBridge` JavaScript
// channel the host registers on its WebView.
//
// There is deliberately NO purchase message and no code path that could grow
// one. Selling a digital good inside a Play-distributed app outside Google
// Play Billing is a store-policy violation whose enforcement risk lands on the
// HOST's store account, not ours. See the agent brief §8.

/** Names the host allows through to its analytics pipeline. */
export const ANALYTICS_EVENTS = {
  matchJoinRequested: 'nouns.match.join_requested',
  matchJoined: 'nouns.match.joined',
  matchJoinFailed: 'nouns.match.join_failed',
  lobbyViewed: 'nouns.lobby.viewed',
  roundStarted: 'nouns.round.started',
  roundSubmitted: 'nouns.round.submitted',
  resultsViewed: 'nouns.results.viewed',
  matchEnded: 'nouns.match.ended',
  reconnectStarted: 'nouns.connection.reconnect_started',
  reconnectSucceeded: 'nouns.connection.reconnect_succeeded',
  reconnectFailed: 'nouns.connection.reconnect_failed',
  sessionExchangeFailed: 'nouns.session.exchange_failed',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Everything this renderer can say to the host. Closed union on purpose. */
export type BridgeMessage =
  | { type: 'ready' }
  | { type: 'score'; score: number; sessionId: string; matchId: string; sig?: string; issuedAt?: number }
  | { type: 'analytics'; event: AnalyticsEvent; props?: Record<string, string | number | boolean> }
  | { type: 'exit' }
  | { type: 'open_full_game'; url: string };

/** The host's injected channel, when we are running inside the app. */
interface HostChannel {
  postMessage(json: string): void;
}

declare global {
  interface Window {
    NounsHuntBridge?: HostChannel;
  }
}

/** True when a host app is listening — false in a plain browser tab. */
export function hasHost(): boolean {
  return typeof window.NounsHuntBridge?.postMessage === 'function';
}

/**
 * Sends one message to the host.
 *
 * Never throws and never awaits. A bridge failure must not be able to take
 * down a live match — the game is playable with the host deaf to it, and the
 * host is the one surface we cannot debug from here.
 */
export function send(message: BridgeMessage): void {
  const json = JSON.stringify(message);
  try {
    window.NounsHuntBridge?.postMessage(json);
  } catch (err) {
    console.warn('[bridge] host rejected a message', message.type, err);
  }
  if (!hasHost()) console.debug('[bridge:no-host]', json);
}

/** Tells the host to hide its loading state. Safe to call more than once. */
export function ready(): void {
  send({ type: 'ready' });
}

/** Asks the host to pop this screen off its navigation stack. */
export function exit(): void {
  send({ type: 'exit' });
}

export function analytics(
  event: AnalyticsEvent,
  props?: Record<string, string | number | boolean>,
): void {
  send(props ? { type: 'analytics', event, props } : { type: 'analytics', event });
}

/**
 * Match ids this renderer has already reported a score for.
 *
 * The host is idempotent too (it keys on `matchId`), but belt-and-braces is
 * cheap here and the failure it prevents — a rider's score double-posting to
 * the weekly leaderboard — is one nobody would notice until the board is
 * already wrong.
 */
const reported = new Set<string>();

/**
 * Reports a completed match's score exactly once per match.
 *
 * [sessionId] is the value the host will echo back to its own
 * `/games/scores` endpoint, mirroring what the 2048 embed does today.
 * [sig] is the server's HMAC over (partner_user_id, match_id, score,
 * issued_at) when the server sent one; the host may ignore it today and
 * verify it later without a protocol change.
 *
 * Returns true when the message was sent, false when it was a duplicate.
 */
export function reportScore(args: {
  score: number;
  sessionId: string;
  matchId: string;
  sig?: string;
  /** Seconds. Required for the host to verify [sig] — it is part of the claim. */
  issuedAt?: number;
}): boolean {
  if (reported.has(args.matchId)) {
    console.debug('[bridge] suppressed duplicate score for', args.matchId);
    return false;
  }
  reported.add(args.matchId);
  send({
    type: 'score',
    score: args.score,
    sessionId: args.sessionId,
    matchId: args.matchId,
    ...(args.sig ? { sig: args.sig } : {}),
    ...(args.issuedAt ? { issuedAt: args.issuedAt } : {}),
  });
  return true;
}

/** Test seam — the reported-match set is module state by design. */
export function resetReportedScores(): void {
  reported.clear();
}
